import React from 'react';
import Table from '../../components/ui/Table.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { useApi } from '../../hooks/useApi.js';
import { Link } from '../../router.jsx';

const semesterOptions = [
  { value: '', label: 'All semesters' },
  { value: 'Fall', label: 'Fall' },
  { value: 'Spring', label: 'Spring' },
  { value: 'Summer', label: 'Summer' }
];

const sortMap = {
  cin: { asc: 'cin_asc', desc: 'cin_desc' },
  name: { asc: 'name_asc', desc: 'name_desc' },
  email: { asc: 'email_asc', desc: 'email_desc' },
  start: { asc: 'start_asc', desc: 'start_desc' },
  status: { asc: 'status_asc', desc: 'status_desc' }
};

function formatStartTerm(student) {
  const semester = (student.start_semester || '').trim();
  const year = Number(student.start_year) > 0 ? String(student.start_year) : '';
  return [semester, year].filter(Boolean).join(' ') || '—';
}

export default function StaffStudents() {
  const [query, setQuery] = React.useState('');
  const [startSemester, setStartSemester] = React.useState('');
  const [startYear, setStartYear] = React.useState('');
  const [sort, setSort] = React.useState('cin_asc');
  const [offset, setOffset] = React.useState(0);
  const limit = 25;
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    q: query,
    sort
  });
  const normalizedStartYear = startYear.trim();
  if (startSemester) params.set('start_semester', startSemester);
  if (normalizedStartYear.length === 4) params.set('start_year', normalizedStartYear);
  const path = `/api/students?${params.toString()}`;
  const { data, loading, error } = useApi(path, [query, startSemester, startYear, sort, offset]);
  const { data: me } = useApi('/api/me');
  const canAddStudent = !!me?.user && (
    me.user.admin_privilege === 1
    || me.user.chair_privilege === 1
    || me.user.edit_privilege === 1
  );
  const items = data?.items || [];

  const headerSortState = (key) => {
    const config = sortMap[key];
    if (!config) return 'none';
    if (sort === config.asc) return 'asc';
    if (sort === config.desc) return 'desc';
    return 'none';
  };

  const toggleSort = (key) => {
    const config = sortMap[key];
    if (!config) return;
    setSort((prev) => (prev === config.asc ? config.desc : config.asc));
    setOffset(0);
  };

  const sortableHeader = (key, label) => {
    const state = headerSortState(key);
    const indicator = state === 'asc' ? 'ASC' : state === 'desc' ? 'DESC' : '';
    const nextDirection = state === 'asc' ? 'descending' : 'ascending';
    return (
      <button
        className={`table-sort ${state === 'none' ? '' : 'table-sort-active'}`}
        type="button"
        onClick={() => toggleSort(key)}
        aria-label={`Sort by ${label} ${nextDirection}`}
      >
        <span>{label}</span>
        <span className="table-sort-indicator" aria-hidden="true">{indicator}</span>
      </button>
    );
  };

  const columns = [
    { key: 'cin', label: sortableHeader('cin', 'CIN') },
    { key: 'name', label: sortableHeader('name', 'Student') },
    { key: 'email', label: sortableHeader('email', 'Email') },
    { key: 'start', label: sortableHeader('start', 'Start Term') },
    { key: 'status', label: sortableHeader('status', 'Status') }
  ];

  const tableData = items.map((student) => ({
    cin: <Link to={`/staff/students/${student.id}`}>{student.cin}</Link>,
    name: <Link to={`/staff/students/${student.id}`}>{`${student.first_name || ''} ${student.last_name || ''}`.trim() || student.username || '—'}</Link>,
    email: student.username || student.personal_email || '—',
    start: formatStartTerm(student),
    status: student.ban === 1 ? <Badge tone="warning">Hold</Badge> : <Badge tone="success">Active</Badge>
  }));

  const filters = (
    <>
      <input
        className="input table-search"
        type="search"
        placeholder="Search by name, CIN, or email"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOffset(0);
        }}
      />
      <select
        className="input table-filter-select"
        aria-label="Filter by start semester"
        value={startSemester}
        onChange={(event) => {
          setStartSemester(event.target.value);
          setOffset(0);
        }}
      >
        {semesterOptions.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <input
        className="input table-filter-year"
        type="text"
        inputMode="numeric"
        maxLength={4}
        aria-label="Filter by start year"
        placeholder="Start year"
        value={startYear}
        onChange={(event) => {
          const digits = event.target.value.replace(/\D/g, '').slice(0, 4);
          setStartYear(digits);
          setOffset(0);
        }}
      />
      {startSemester || startYear ? (
        <button
          className="btn btn-ghost btn-sm"
          type="button"
          onClick={() => {
            setStartSemester('');
            setStartYear('');
            setOffset(0);
          }}
        >
          Clear
        </button>
      ) : null}
    </>
  );

  const pagination = {
    offset,
    limit,
    hasNext: items.length === limit,
    onPageChange: setOffset
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Students</h1>
          <p className="subtle">Search, filter, and update student records.</p>
        </div>
        {canAddStudent ? <Link to="/staff/students/new" className="btn btn-primary">Add Student</Link> : null}
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      <Table
        columns={columns}
        data={tableData}
        filters={filters}
        pagination={pagination}
        emptyMessage={loading ? 'Loading...' : 'No students found.'}
      />
    </div>
  );
}
